<template>
    <div>
        <test-template :testName="testName" :testId="testId" :test="test" multidimensional>
            <template #questions>
                <div v-if="cQ && cQ.answer.length === 0">
                    <b-card class="neu-card">
                        <h2>{{cQ.question}}</h2>
                        <b-row>
                            <b-col class="d-flex justify-content-center">
                                <div class="d-inline-flex">
                                    <b-row>
                                        <b-col cols="12" sm="12" md="6" lg="6">
                                            <base-input class="inputAnswer" type="number" v-model="sleepQualityTime.hour" :placeholder="$t('tests.sleep-quality.hours')" :min="0" :max="23">
                                                <template slot="append">
                                                    <span>h</span>
                                                </template>
                                            </base-input>
                                        </b-col>
                                        <b-col cols="12" sm="12" md="6" lg="6">
                                            <base-input class="inputAnswer" type="number" v-model="sleepQualityTime.minutes" :placeholder="$t('tests.sleep-quality.minutes')" :min="0" :max="59">
                                            <template slot="append">
                                                    <span>min</span>
                                                </template>
                                            </base-input>
                                        </b-col>
                                    </b-row>
                                </div>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col xs="12" lg="8" class="px-0 pt-1 px-md-5 pt-md-3 float-right m-auto">
                                <transition name="fade" appear>
                                    <button v-if="sleepQualityTime.hour && sleepQualityTime.minutes && sleepQualityTime.hour >= 0  && sleepQualityTime.minutes >= 0 && sleepQualityTime.hour < 24 && sleepQualityTime.minutes < 60" type="button" class="btn btn-primary w-100" @click.once="calculateInputResults()">
                                        <span class="text-center">{{ sleepQualityTime.hour  }} {{ $t('tests.sleep-quality.hours')}} {{ sleepQualityTime.minutes }} {{ $t('tests.sleep-quality.minutes')}}</span>
                                        <span class="float-right m-auto"><base-icon src="/static-appopensalud/img/icons/morillas-svg-icons/primary/arrow.svg" color="white"/></span>
                                    </button>
                                </transition>
                            </b-col>
                        </b-row>
                    </b-card>
                </div>
                <div v-else>
                    <question-card :question="cQ" v-model="questionResult" :progress="$store.state.tests.currentSlide" :questionnaireLength="$store.state.tests.test.length"  @answerQuestion="() => { $store.commit('tests/incrementSlide') }" @submitQuestionnaire="saveTestResults"></question-card>
                </div>
            </template>
            <template #results>
                <div key="topGraph" class="first">
                    <h3 class="subtitle-card text-center">{{ $t('tests.testResults') }} - {{ testName.toUpperCase() }}</h3>
                    <column-chart :lastYearResults="$store.state.tests.previousTestResults.slice(-12)" :testName="testName" class="justify-content-center"></column-chart>
                </div>
                <div key="recapCard" class="second">
                    <h4>{{ $t('tests.results') }} {{ month }}</h4>
                    <b-card class="neu-card mean-result col-lg-6">
                        <h3 class="subtitle-card"> {{ $t('tests.sleep-quality.pittsburg') }}</h3>
                        <b-row class="h-100">
                            <b-col class="my-auto">
                                <gauge-chart :labels="[testName]" :result="DBResults.generalResult"></gauge-chart>
                            </b-col>
                        </b-row>
                    </b-card>
                </div>
                <div key="dimensions" class="third">
                    <h4>{{ $t('tests.dimensions') }}</h4>
                    <div class="col-lg-12 results-container">
                        <div v-for="d in DBResults.dimensions" :key="d.name" class="col-lg-8 results-card">
                            <b-col>
                                <b-card class="neu-card" v-b-toggle="d.id" :title="$t(d.name)">
                                    <div>
                                        <b-icon-circle-fill :style="analysisColor(d.result)"></b-icon-circle-fill> <span>{{ dimensionAnalysis(d.result, d.id) }}</span>
                                    </div>
                                    <div style="margin-top:1%;margin-bottom:-2%">
                                        <span class="text-center accordion-arrow-closed">
                                            <b-icon-chevron-down></b-icon-chevron-down>
                                        </span>
                                        <span class="text-center accordion-arrow-open">
                                            <b-icon-chevron-up></b-icon-chevron-up>
                                        </span>
                                        <b-collapse :id="d.id" class="mt-2 text-left" accordion="accordion">
                                            <p class="card-text text-justify" v-html="$t(`tests.sleep-quality.dimensionsDescriptions.${d.id}`)"></p>
                                        </b-collapse>
                                    </div>
                                </b-card>
                            </b-col>
                        </div>
                    </div>
                </div>
            </template>
        </test-template>
    </div>
</template>

<script>
import TestTemplate from './TestTemplate.vue'
import { BCollapse, BProgress } from 'bootstrap-vue'
import { GREEN_COLOUR, YELLOW_COLOUR, RED_COLOUR } from '@/constants/colours.js'
import selfEvaluationTestMixin from '@/mixins/selfEvaluationTest.mixin'
import { TEST_SLEEP_QUALITY } from '@/constants/patientServices'
import BaseRadioGroup from '@/components/base/forms/BaseRadioGroup.vue'
import BaseInput from '@/components/base/forms/BaseInput.vue'

export default {
    mixins: [selfEvaluationTestMixin],
    components: {
        'test-template': TestTemplate,
        BCollapse,
        BProgress,
        BaseInput,
        'gauge-chart': () => import('@/components/charts/GaugeChart.vue'),
        'column-chart': () => import('@/components/charts/ColumnChart.vue'),
        'radio-button': BaseRadioGroup
    },
    data () {
        const values = {
            timesAWeek: [
                {value: 0, text: this.$t('tests.answers.notInTheLastMonth')},
                {value: 1, text: this.$t('tests.answers.lessThanOnceAWeek')},
                {value: 2, text: this.$t('tests.answers.onceAWeek')},
                {value: 3, text: this.$t('tests.answers.moreThanOnceAWeek')}
            ],
            problems: [
                {value: 0, text: this.$t('tests.answers.noProblem')},
                {value: 1, text: this.$t('tests.answers.lightProblem')},
                {value: 2, text: this.$t('tests.answers.problem')},
                {value: 3, text: this.$t('tests.answers.seriousProblem')}
            ],
            timeSpent: [
                {value: 0, text: this.$t('tests.answers.lessThan15minutes')},
                {value: 1, text: this.$t('tests.answers.15to30minutes')},
                {value: 2, text: this.$t('tests.answers.30to60minutes')},
                {value: 3, text: this.$t('tests.answers.moreThan60minutes')}
            ],
            goodToBad: [
                {value: 3, text: this.$t('tests.answers.veryBad')},
                {value: 2, text: this.$t('tests.answers.prettyBad')},
                {value: 1, text: this.$t('tests.answers.prettyGood')},
                {value: 0, text: this.$t('tests.answers.veryGood')}
            ]
        }
        const test = [
            {
                dimension: 'tests.sleep-quality.dimensions.subjectiveQuality',
                qs: ['q15']
            },
            {
                dimension: 'tests.sleep-quality.dimensions.sleepLatency',
                qs: ['q2', 'q5']
            },
            {
                dimension: 'tests.sleep-quality.dimensions.sleepDuration',
                qs: ['q4']
            },
            {
                dimension: 'tests.sleep-quality.dimensions.sleepEfficiency',
                qs: ['q3', 'q1']
            },
            {
                dimension: 'tests.sleep-quality.dimensions.sleepPerturbations',
                qs: ['q6', 'q7', 'q8', 'q9', 'q10', 'q11', 'q12', 'q13', 'q14']
            },
            {
                dimension: 'tests.sleep-quality.dimensions.medicationToSleep',
                qs: ['q16']
            },
            {
                dimension: 'tests.sleep-quality.dimensions.dayDisfunction',
                qs: ['q17', 'q18']
            }
        ]
        return {
            test: test,
            values: values,
            testId: TEST_SLEEP_QUALITY,
            testName: this.$t('tests.sleep-quality.title'),
            sleepQualityTime: {}
        }
    },
    created () {
        this.createQuestions()
    },
    methods: {
        createQuestions () {
            var i = 1
            this.test.map(dimension => {
                dimension.id = `apartado-${i}`
                var questions = []
                dimension.qs.map(x => questions.push({
                    id: x,
                    question: this.$t(`tests.sleep-quality.questions.${x}`),
                    answer: x === 'q1' || x === 'q3' || x === 'q4' ? [] : x === 'q18' ? this.values.problems : x === 'q2' ? this.values.timeSpent : x === 'q15' ? this.values.goodToBad : this.values.timesAWeek,
                    result: null
                }))
                i = i + 1
                this.$set(dimension, 'questions', questions)
            })
        },
        saveTestResults () {
            var dimensions = []
            var q4 = 0
            for (let dimension of this.test) {
                var result = 0
                let id = dimension.id
                switch (true) {
                    case (id === 'apartado-1' || id === 'apartado-6'):
                        result = dimension.questions[0].result
                        break
                    case (id === 'apartado-2' || id === 'apartado-7'):
                        result = Math.floor(dimension.questions[0].result + dimension.questions[1].result / 2)
                        break
                    case (id === 'apartado-3'):
                        q4 = dimension.questions[0].result
                        result = q4 > 7 * 60 ? 0 : q4 > 6 * 60 ? 1 : q4 >= 5 * 60 ? 2 : 3
                        break
                    case (id === 'apartado-5'):
                        let sum = 0
                        for (let question of dimension.questions) {
                            sum = sum + question.result
                        }
                        result = sum > 18 ? 3 : sum > 9 ? 2 : sum > 0 ? 1 : 0
                        break
                    case (id === 'apartado-4'):
                        let q1 = dimension.questions[1].result
                        let q3 = dimension.questions[0].result
                        let bedtime = q3 > q1 ? q3 - q1 : q1 > q3 ? 24 * 60 - q1 + q3 : 24 * 60
                        let e = (q4 / bedtime) * 100
                        result = e >= 85.0 ? 0 : e >= 75.0 ? 1 : e >= 65 ? 2 : 3
                        break
                }
                var dimensionResult = {'result': result}
                dimensions.push({...dimensionResult, 'id': id, 'name': dimension.dimension})
            }
            this.save(dimensions, 100 - this.percentResult(dimensions.map(x => x.result).reduce((x, y) => x + y, 0), 21))
        },
        calculateInputResults () {
            this.cQ.result = parseInt(this.sleepQualityTime.hour) * 60 + parseInt(this.sleepQualityTime.minutes)
            this.sleepQualityTime = {}
            if (this.$store.state.tests.currentSlide <= this.test.length - 2) {
                this.$store.commit('tests/incrementSlide')
            } else {
                this.saveTestResults()
            }
        },
        dimensionAnalysis (result, dimId) {
            if (dimId === 'apartado-5' || dimId === 'apartado-6' || dimId === 'apartado-7') {
                return result === 0 ? this.$t('tests.answers.veryLow') : result === 1 ? this.$t('tests.answers.low') : result === 2 ? this.$t('tests.answers.high') : this.$t('tests.answers.veryHigh')
            } else {
                return result === 0 ? this.$t('tests.answers.veryHigh') : result === 1 ? this.$t('tests.answers.high') : result === 2 ? this.$t('tests.answers.low') : this.$t('tests.answers.veryLow')
            }
        },
        analysisColor (r) {
            return r === 0 ? `color: ${GREEN_COLOUR}` : r === 2 || r === 1 ? `color: ${YELLOW_COLOUR}` : `color: ${RED_COLOUR}`
        }
    }
}
</script>


// WEBPACK FOOTER //
// src/components/views/selfEvaluationTests/SleepQualityTest.vue